'use client';

import { useState, useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useContainerStatus } from '@/hooks/useContainerStatus';

/**
 * Tiny header indicator showing container boot status.
 *
 * - Booting: amber pulsing dot with "AI preparing..." tooltip
 * - Ready: green dot that fades after 3s
 * - Error: red dot with "AI unavailable" tooltip
 * - Hidden when feature flag is off or container not applicable
 */
export function ContainerStatusIndicator() {
  const { isBooting, isReady, isError, isEnabled, message } = useContainerStatus();
  const [visible, setVisible] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);

  // Show indicator when booting or on error; fade out after ready
  useEffect(() => {
    if (isBooting || isError) {
      setVisible(true);
      setFadingOut(false);
    } else if (isReady) {
      setVisible(true);
      setFadingOut(false);
      // Fade out after 3 seconds
      const timer = setTimeout(() => {
        setFadingOut(true);
      }, 3000);
      // Fully hide after fade animation completes
      const hideTimer = setTimeout(() => {
        setVisible(false);
      }, 3500);
      return () => {
        clearTimeout(timer);
        clearTimeout(hideTimer);
      };
    }
  }, [isBooting, isReady, isError]);

  if (!isEnabled || !visible) {
    return null;
  }

  const tooltipText = isError
    ? 'AI unavailable'
    : isReady
      ? 'AI ready'
      : message || 'AI preparing...';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'flex items-center gap-1.5 px-2 py-1.5 rounded-full text-xs font-medium cursor-default transition-opacity duration-500',
            fadingOut && 'opacity-0',
            isError && 'bg-red-100 text-red-700',
            isBooting && 'bg-amber-100 text-amber-700',
            isReady && !fadingOut && 'bg-sage/10 text-sage',
          )}
        >
          <span
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              isError && 'bg-red-500',
              isBooting && 'bg-amber-500 animate-pulse',
              isReady && 'bg-sage',
            )}
          />
          <span className="hidden sm:inline">
            {isError ? 'AI unavailable' : isReady ? 'AI ready' : 'AI preparing...'}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium">{tooltipText}</p>
        {isBooting && (
          <p className="text-xs text-slate-400">Your AI team is starting up</p>
        )}
        {isError && (
          <p className="text-xs text-slate-400">
            AI features will fall back to standard processing
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export default ContainerStatusIndicator;
